/**
 * Porten mot identitetsleverandøren (tech-decisions.md, «M2.1»). Panelet kjenner
 * bare dette grensesnittet; `supabase.ts` er den ene adapteren, og testene gir en
 * fake. Det er også det som gjør v2 (OIDC-føderering for adminbrukere, K22) til
 * et bytte av én fil.
 *
 * Nivåene følger Supabases begreper: `aal1` er innlogget med én faktor, `aal2` er
 * bekreftet med en andre faktor. Om `aal2` KREVES er leietakerens valg (K8, 2026-09-21):
 * standard er at det kreves, og leietakerens eier kan slå det av. Panelet spør
 * `Sesjonsspoerring` om svaret; API-et håndhever det uavhengig av hva klienten mener.
 */
export type Aal = 'aal1' | 'aal2';

/**
 * Svaret fra luken `GET /v1/admin/session` — det ENESTE kallet panelet får gjøre før
 * det vet om tofaktor kreves. Nøyaktig to felter, og listen vokser ikke: trenger panelet
 * noe mer om seg selv, hører det hjemme bak gaten, i `/v1/admin/me`.
 */
export interface Sesjonskrav {
  /** Krever leietakeren `aal2` av adminbrukerne sine? Standard for en ny leietaker er `true`. */
  adminMfaRequired: boolean;
  /** Sesjonens harde utløp (første innlogging + 8 t). Uavhengig av tofaktorkravet. */
  sessionExpiresAt: string;
}

/**
 * Porten mot luken, ved siden av `AdminAuth` og av samme grunn: `AuthProvider` er ikke
 * `src/api/**` og rører derfor aldri den genererte klienten (vakt 2). Adapteren er
 * `src/api/sesjon.ts`; testene gir en fake gjennom `fetch`.
 *
 * Tokenet sendes inn, ikke hentet: provideren har sesjonen i hånden når den spør, og et
 * kall tilbake i provideren for å hente det samme tokenet ville vært en runde for mye.
 *
 * Kaster ved alt annet enn et gyldig svar. Den som kaller skal lese et kast som at
 * **kravet gjelder** — aldri som at det ikke gjør det.
 */
export type Sesjonsspoerring = (token: string) => Promise<Sesjonskrav>;

/**
 * De to andrefaktorene panelet kjenner. Begge gir aal2, og en konto kan ha begge.
 *
 * `enhet` er WebAuthn — Windows Hello, Touch ID eller en sikkerhetsnøkkel. Den er
 * phishing-resistent, og dermed STERKERE enn en engangskode fra en app: nøkkelen er
 * bundet til panelets domene, så en etterligning av innloggingssiden får ingenting ut
 * av den. Den erstatter likevel ingenting. `app` blir stående av to grunner: en bruker
 * som satte opp appen i går skal ikke låses ute i dag, og enheten finnes ikke i alle
 * nettlesere.
 *
 * Panelets ord er «denne enheten» og «koden fra appen». «WebAuthn», «passkey» og
 * «faktor» er utviklerens ord og står aldri på en skjerm.
 */
export interface Faktorer {
  /** Autentiseringsapp (TOTP) er registrert og verifisert. */
  app: boolean;
  /** En enhet (WebAuthn) er registrert og verifisert. */
  enhet: boolean;
  /**
   * Én oppføring per verifisert metode, med id-en og når den ble lagt til.
   *
   * Hvorfor både `app`/`enhet` OG denne lista: utfordringen stiller et ja/nei-spørsmål —
   * «hvilket spor skal jeg tegne?» — og skal ikke lete i en liste for å svare på det.
   * «Min innlogging» stiller et annet: «hva har jeg, når la jeg det til, og hva kan jeg
   * fjerne?». Ett kall til leverandøren, to spørsmål, og begge besvart uten at noen av
   * skjermene må regne ut den andres svar.
   */
  liste: Faktoroppforing[];
}

/** En verifisert metode slik «Min innlogging» trenger den: hvilken, når, og id-en å fjerne den med. */
export interface Faktoroppforing {
  /** Leverandørens id. Den eneste veien til å fjerne metoden — panelet lager den aldri selv. */
  id: string;
  type: 'app' | 'enhet';
  /** ISO-tidspunkt fra leverandøren. Formateres på skjermen, aldri her. */
  lagtTil: string;
}

export interface AdminSesjon {
  /** Bearer-tokenet til API-et. Leses friskt per kall og lagres aldri utenfor adapteren. */
  accessToken: string;
  /** Sesjonens nivå nå. */
  aal: Aal;
  /** Nivået sesjonen KAN nå: `aal2` betyr at brukeren har en verifisert faktor å bekrefte med. */
  nesteAal: Aal;
  /**
   * Adressen kontoen er logget inn med, slik leverandøren kjenner den. Vises kun til brukeren
   * selv, i skallet (DD-48): svaret på «hvilken konto er jeg inne med?» er leverandørens, ikke
   * API-ets — `/v1/admin/me` bærer ingen e-post.
   */
  epost?: string;
}

export interface TotpOppsett {
  faktorId: string;
  /** QR-koden som data-URI (SVG) til autentiseringsappen. */
  qr: string;
  /** Nøkkelen som tekst, for den som ikke kan skanne. */
  noekkel: string;
  /**
   * `otpauth://`-lenken leverandøren alt lager — samme oppsett som QR-en koder.
   * Båret hit i R6.2 (tofaktor-konsept v0.1, P3 a) fordi en berøringsenhet har
   * autentiseringsappen på SAMME enhet som koden: der kan den ikke skannes, og
   * «Åpne i autentiseringsappen» er alternativet i stedet for nøkkelen.
   */
  uri: string;
}

export interface AdminAuth {
  hentSesjon(): Promise<AdminSesjon | null>;
  /** Kalles når sesjonen endrer seg (innlogging, fornyelse, utlogging, annen fane). Returnerer avmelding. */
  paaEndring(cb: () => void): () => void;
  /** E-post med engangskode. Oppretter aldri en bruker — setet gis av eier, ikke av innloggingen. */
  sendKode(epost: string): Promise<void>;
  bekreftKode(epost: string, kode: string): Promise<void>;
  loggInnMedGoogle(): Promise<void>;
  /** Starter TOTP-oppsett for en bruker uten verifisert faktor. */
  startOppsett(): Promise<TotpOppsett>;
  /** Bekrefter oppsettet med første kode; faktoren blir verifisert og sesjonen aal2. */
  bekreftOppsett(faktorId: string, kode: string): Promise<void>;
  /** Bekrefter en eksisterende faktor; sesjonen blir aal2. */
  bekreftUtfordring(kode: string): Promise<void>;

  /**
   * Kan DETTE miljøet bekrefte med enheten i det hele tatt? Synkron med vilje: svaret
   * er en egenskap ved nettleseren, ikke et nettkall, og skjermen må vite det før den
   * tegner noe — et primærvalg som ikke kan innfris er verre enn ingen valg.
   */
  stoetterEnhet(): boolean;

  /** Hvilke faktorer kontoen har verifisert. Utfordringen velger spor på svaret. */
  hentFaktorer(): Promise<Faktorer>;

  /** Registrerer denne enheten som faktor. Hele seremonien i ett kall; sesjonen blir aal2. */
  settOppEnhet(): Promise<void>;

  /** Bekrefter med en registrert enhet; sesjonen blir aal2. */
  bekreftMedEnhet(): Promise<void>;

  /**
   * Avregistrerer én verifisert metode hos leverandøren. Irreversibel: en fjernet enhet må
   * registreres på nytt, og en fjernet app må settes opp med en NY hemmelighet.
   *
   * Porten håndhever ingen «minst én igjen»-regel — den regelen er skjermens, fordi den er
   * det brukeren skal LESE før hun trykker, ikke en feil hun skal møte etterpå. Serveren
   * ville uansett latt henne fjerne den siste og sendt henne rett i oppsettet neste gang.
   */
  fjernFaktor(id: string): Promise<void>;

  loggUt(): Promise<void>;
}

/** Feil fra leverandøren, med melding panelet kan vise. Aldri innloggingsdata i meldingen. */
export class AuthFeil extends Error {
  constructor(melding: string) {
    super(melding);
    this.name = 'AuthFeil';
  }
}
